import { useContext } from 'react';
import { Box, Grid, Typography, Stack, Tabs, Tab } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useTranslation, Trans } from 'react-i18next';
import { Redirect, useParams } from 'react-router-dom';
import { formatDate } from 'date-fns';

import { InlineStatus } from 'components/InlineStatus';
import {
  DrawerStickyHeader,
  useDrawerStickyHeader,
} from 'components/DrawerStickyHeader';
import { MapEntityDrawerContext } from 'features/mapping/components/MapEntityDrawer';
import { UserStatusChip } from 'components/UserStatusChip';
import { useMapEntityDetailsTabs } from 'features/mapping/components/MapEntityDetailsLayout';
import { MapEntityDrawerHeader } from 'features/mapping/components/MapEntityDrawerHeader';
import { useExternalGeoEvent } from 'hooks/useExternalGeoEvents';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useTabs } from 'hooks/useTabs';
import { ExternalGeoEventAck } from 'pages/ExternalGeoEventDetails/components/ExternalGeoEventAck/ExternalGeoEventAck';
import { ExternalGeoEventApprove } from 'pages/ExternalGeoEventDetails/components/ExternalGeoEventApprove';
import { ExternalGeoEventInfo } from 'pages/ExternalGeoEventDetails/components/ExternalGeoEventInfo';
import { ExternalGeoEventBroadcastify } from 'pages/ExternalGeoEventDetails/components/ExternalGeoEventBroadcastify';
import { ExternalGeoEventRule } from 'pages/ExternalGeoEventDetails/components/ExternalGeoEventRule';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { getTimeAgo, SHORT_MONTH_DAY_12HOUR_FORMAT } from 'shared/dates';
import { useAuthState } from 'state/useAuthState';
import { ExternalGeoNameMap } from '../../constants';
import { getHeadline } from './ExternalGeoEventDetails.utils';

const TAB_PANEL_ID_PREFIX = 'external-event-details';

// Mirrors the wildfire card's sticky-header height.
const BASE_TABS_TOP = 240;

export const ExternalGeoEventDetails = (): JSX.Element => {
  const { externalGeoEventId } = useParams<{ externalGeoEventId: string }>();
  const { t } = useTranslation();
  const { scrollElementRef } = useContext(MapEntityDrawerContext);
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const {
    externalGeoEvent: event,
    isExternalGeoEventFetchError,
    isExternalGeoEventLoading,
  } = useExternalGeoEvent(externalGeoEventId);
  const { contentHeight, onStickyChange, onStickyContentHeightChange } =
    useDrawerStickyHeader();
  const { onChangeTab } = useMapEntityDetailsTabs({ baseTop: BASE_TABS_TOP });
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  const tabData = [
    {
      content: event && <ExternalGeoEventInfo event={event} />,
      enabled: true,
      label: t('externalGeoEvent.tabs.info'),
      tabId: 'info',
    },
    {
      content: event && <ExternalGeoEventBroadcastify event={event} />,
      enabled: true,
      label: pinImprovements
        ? t('feeds.label')
        : t('externalGeoEvent.tabs.broadcastify'),
      adornment: pinImprovements ? (
        <UserStatusChip type="pro" size="small" />
      ) : undefined,
      tabId: pinImprovements ? 'feeds' : 'broadcastify',
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    ariaLabel: t('externalGeoEvent.tabs.ariaLabel'),
    idPrefix: TAB_PANEL_ID_PREFIX,
    tabData,
    onChangeTab,
  });

  //  If they don't have permission to be here, send them away
  if (!isInternalUser) {
    return <Redirect to={{ pathname: '/' }} />;
  }

  //  Check for loading or errors
  if (isExternalGeoEventLoading) {
    return <InlineStatus status="loading" />;
  }
  if (isExternalGeoEventFetchError || !event) {
    return <InlineStatus status="error" />;
  }

  const sourceName = ExternalGeoNameMap[event.externalSource];
  const headline = getHeadline(event, t);

  return (
    <>
      <Helmet>
        <title>{t('home.externalGeoEventTitle', { name: sourceName })}</title>
      </Helmet>

      <Grid container sx={{ width: '100%' }}>
        <Grid size={12}>
          <DrawerStickyHeader
            scrollContainer={scrollElementRef?.current as HTMLElement}
            onStickyChange={onStickyChange}
            onStickyContentHeightChange={onStickyContentHeightChange}
          >
            <DrawerStickyHeader.ContentContainer>
              <Box>
                <MapEntityDrawerHeader>{sourceName}</MapEntityDrawerHeader>
                {headline && <Typography>{headline}</Typography>}

                {event.data.county && (
                  <Typography variant="body2" color="textSecondary">
                    {event.data.county}
                  </Typography>
                )}
              </Box>
            </DrawerStickyHeader.ContentContainer>
          </DrawerStickyHeader>

          <Stack sx={{ py: 1, px: 2 }}>
            <Typography>
              {t('externalGeoEvent.updated', {
                time: getTimeAgo(event.dateModified),
              })}
            </Typography>
            <GeoEventMetadataText>
              <Trans
                i18nKey="externalGeoEvent.createdBy"
                values={{
                  name: sourceName,
                  timeAgo: getTimeAgo(event.dateCreated),
                  date: formatDate(
                    new Date(event.dateCreated),
                    SHORT_MONTH_DAY_12HOUR_FORMAT,
                  ),
                }}
              >
                Created by <b>PulsePoint</b> • 1 day ago • Aug 1, 12:00 PM
              </Trans>
            </GeoEventMetadataText>
          </Stack>

          <ExternalGeoEventRule event={event} />
          <ExternalGeoEventApprove event={event} />
          <ExternalGeoEventAck event={event} />
        </Grid>

        <Grid size={12}>
          <DrawerStickyHeader
            scrollContainer={scrollElementRef?.current as HTMLElement}
            topOffset={contentHeight * -1}
            stickyStyle={{ marginTop: contentHeight }}
          >
            <Tabs {...tabsProps}>
              {tabProps.map((props) => {
                const { key, ...rest } = props;
                return <Tab key={key} {...rest} />;
              })}
            </Tabs>
          </DrawerStickyHeader>
        </Grid>
      </Grid>

      {tabPanels}
    </>
  );
};
